/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { CharacterLimitPlugin } from '@lexical/react/LexicalCharacterLimitPlugin';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { CollaborationPlugin } from '@lexical/react/LexicalCollaborationPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HashtagPlugin } from '@lexical/react/LexicalHashtagPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import * as React from 'react';
import { useEffect, useState, useCallback, useRef } from 'react';
import { CAN_USE_DOM } from './shared/canUseDOM';

import { createWebsocketProvider } from './collaboration';
import { useSettings } from './context/SettingsContext';
import { useSharedHistoryContext } from './context/SharedHistoryContext';
import ActionsPlugin from './plugins/ActionsPlugin';
import AutocompletePlugin from './plugins/AutocompletePlugin';
import AutoEmbedPlugin from './plugins/AutoEmbedPlugin';
import AutoLinkPlugin from './plugins/AutoLinkPlugin';
import CodeActionMenuPlugin from './plugins/CodeActionMenuPlugin';
import CodeHighlightPlugin from './plugins/CodeHighlightPlugin';
import CollapsiblePlugin from './plugins/CollapsiblePlugin';
import CommentPlugin from './plugins/CommentPlugin';
import ComponentPickerPlugin from './plugins/ComponentPickerPlugin';
import ContextMenuPlugin from './plugins/ContextMenuPlugin';
import DragDropPaste from './plugins/DragDropPastePlugin';
import DraggableBlockPlugin from './plugins/DraggableBlockPlugin';
import EmojiPickerPlugin from './plugins/EmojiPickerPlugin';
import EmojisPlugin from './plugins/EmojisPlugin';
import EquationsPlugin from './plugins/EquationsPlugin';
import ExcalidrawPlugin from './plugins/ExcalidrawPlugin';
import FigmaPlugin from './plugins/FigmaPlugin';
import FloatingLinkEditorPlugin from './plugins/FloatingLinkEditorPlugin';
import FloatingTextFormatToolbarPlugin from './plugins/FloatingTextFormatToolbarPlugin';
import ImagesPlugin from './plugins/ImagesPlugin';
import InlineImagePlugin from './plugins/InlineImagePlugin';
import KeywordsPlugin from './plugins/KeywordsPlugin';
import { LayoutPlugin } from './plugins/LayoutPlugin/LayoutPlugin';
import LinkPlugin from './plugins/LinkPlugin';
import ListMaxIndentLevelPlugin from './plugins/ListMaxIndentLevelPlugin';
import ListRemovePlugin from './plugins/ListRemovePlugin';
import MarkdownShortcutPlugin from './plugins/MarkdownShortcutPlugin';
import { MaxLengthPlugin } from './plugins/MaxLengthPlugin';
import MentionsPlugin from './plugins/MentionsPlugin';
import PageBreakPlugin from './plugins/PageBreakPlugin';
import EnsurePagedRootPlugin from './plugins/EnsurePagedRootPlugin';
import PaginationPlugin from './plugins/PaginationPlugin';
import HeaderFooterSyncPlugin from './plugins/HeaderFooterSyncPlugin';
import PollPlugin from './plugins/PollPlugin';
import ChartPlugin from './plugins/ChartPlugin';
import SpeechToTextPlugin from './plugins/SpeechToTextPlugin';
import TabFocusPlugin from './plugins/TabFocusPlugin';
import TableCellActionMenuPlugin from './plugins/TableActionMenuPlugin';
import TableCellResizer from './plugins/TableCellResizer';
import TableHoverActionsPlugin from './plugins/TableHoverActionsPlugin';
import TableOfContentsPlugin from './plugins/TableOfContentsPlugin';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import TreeViewPlugin from './plugins/TreeViewPlugin';
import TwitterPlugin from './plugins/TwitterPlugin';
import YouTubePlugin from './plugins/YouTubePlugin';
import ContentEditable from './ui/ContentEditable';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $generateHtmlFromNodes } from '@lexical/html';
import { insertEditorInstancesDelta, loadSignatures, setEditorSummary, setIsConflictModalVisible, setIsExternalAllow, setLexicalEditor, setServerData, updateIsSaving, setCurrentEditorData } from '../../app/EditorSlice';
import { useDispatch, useSelector } from 'react-redux';
import { adjustEditorHeightWithSignatures, debounce, fetchIpAddress, getLocationCoordinates, isEffectivelyOffline, removeOfflineData, safeParseEditorState, saveToLocalStorage, showPagebreak } from '../../utils/helper';
import { useParams, useSearchParams } from 'react-router-dom';
import { updateLocalDocumentContent } from '../../utils/localDocuments';
import { padServices } from '../../api.js';
import LexicalSignatures from './LexicalDocumentSignature/signatures';
import { emitLoadData, listenLoadData, listenUpdateAll } from '../../Socket';
import WaterMark from '../WaterMark';

const skipCollaborationInit = false
// @ts-expect-error
// window.parent != null && window.parent.frames.right === window;

function MyOnChangePlugin({ onChange }) {
  const [editor] = useLexicalComposerContext();

  // console.log(editor, "===editor===")
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      onChange(editorState);
    });

  }, [editor, onChange]);
  return null;
};
function MyHtmlPlugin() {

  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isLocalDocument = searchParams.get('local') === 'true';
  const [editor] = useLexicalComposerContext();
  const dispatch = useDispatch();
  const [hasLoaded, setHasLoaded] = useState(false);
  const previousEditorStateRef = useRef(null);
  const documentVersionData = useSelector((state) => state.editor.documentVersionData);
  const isChangeByUser = useSelector((state) => state.editor.isChangeByUser);
  const summaryText = useSelector((state) => state.editor.editorSummary);
  const signatures = useSelector((state) => state.editor.signatures);
  const conflictPreviewVerion = useSelector((state) => state.editor.conflictPreviewVerion);
  const serverData = useSelector((state) => state.editor.serverData);
  const isTemplateDocument = useSelector((state) => state.editor.isTemplateDocument);
  const cachedPreview = useSelector((state) => state.editor.cachedPreview);
  const previewLoadedRef = useRef(false);

  useEffect(() => {
    if (documentVersionData.isVersionChange) {
      const initialEditorState = safeParseEditorState(editor, documentVersionData.versionData.milepadData);
      editor.setEditorState(initialEditorState);
    }
  }, [documentVersionData]);

  const saveEditorStateToDatabase = async (id, htmlString) => {
    try {
      if (isTemplateDocument) {
        // For templates, use the update API with only the updated field
        const payload = {
          data: { milepadData: htmlString },
          templateId: id,
          dispatch,
        };
        await padServices.updateTemplate(payload);
      } else {
        // Normal save
        await padServices.saveToDatabase(id, htmlString, null, dispatch);
      }
    } catch (error) {
      console.error('Failed to save editor state:', error);
    }
  };

  const updateHtmlContent = useCallback(
    debounce((editorState) => {
      const currentEditorStateJSON = JSON.stringify(editorState.toJSON());
      if (previousEditorStateRef.current !== currentEditorStateJSON) {
        // Local documents persist only to localStorage, regardless of connectivity
        // (they have no server counterpart until synced).
        if (isLocalDocument) {
          updateLocalDocumentContent(id, {
            milepadData: currentEditorStateJSON,
            summary: summaryText,
            Signatures: signatures,
          });
          previousEditorStateRef.current = currentEditorStateJSON;
          return;
        }
        const isOffline = isEffectivelyOffline();
        if (isOffline) {
          // Save to localStorage if offline
          saveToLocalStorage(id, {
            milepadData: currentEditorStateJSON,
            summary: summaryText,
            Signatures: signatures,
          });
          return;
        }
        else {
          // Mirror the live editor content so cache write-back captures unsaved edits.
          dispatch(setCurrentEditorData(currentEditorStateJSON));
          dispatch(updateIsSaving({ isSaving: 'Saving...', lastSavedDateTime: '' }));
          editorState.read(() => {
            const htmlString = $generateHtmlFromNodes(editor, null);
            if (htmlString) {
              if (isChangeByUser) {
                emitLoadData({
                  data: currentEditorStateJSON,
                  type: 'editor',
                  documentId: id,
                });
              }
              dispatch(insertEditorInstancesDelta(htmlString));
              saveEditorStateToDatabase(id, currentEditorStateJSON);

            }
          });
        }
        previousEditorStateRef.current = currentEditorStateJSON;
      }
    }, 500),
    [editor, dispatch, id, isChangeByUser, isLocalDocument]
  );

  const loadDataIntoEditor = (data) => {
    const signatures = data?.Signatures || [];
    const initialState = safeParseEditorState(editor, data?.milepadData);
    editor.setEditorState(initialState);
    previousEditorStateRef.current = JSON.stringify(initialState.toJSON());
    dispatch(loadSignatures(signatures));
    adjustEditorHeightWithSignatures(signatures);
    showPagebreak();
    const htmlContent = initialState.read(() => $generateHtmlFromNodes(editor, null));
    if (htmlContent) {
      dispatch(insertEditorInstancesDelta(htmlContent));
    }
    dispatch(setIsExternalAllow(data?.isExternal || false));
    dispatch(setEditorSummary(data?.summary || ""));
    dispatch(setCurrentEditorData(data?.milepadData || null));
  };

  // Instant cached paint: render the cached content ONCE without marking the
  // editor "ready" (hasLoaded stays false) so the fresh serverData below still
  // swaps in when the background fetch resolves.
  useEffect(() => {
    if (
      cachedPreview &&
      Object.keys(cachedPreview).length > 0 &&
      !hasLoaded &&
      !previewLoadedRef.current
    ) {
      previewLoadedRef.current = true;
      loadDataIntoEditor(cachedPreview);
    }
  }, [cachedPreview, hasLoaded, editor]);

  useEffect(() => {
    if (serverData && Object.keys(serverData).length > 0 && !hasLoaded) {
      loadDataIntoEditor(serverData);
      listenLoadData(editor, dispatch);
      listenUpdateAll(id, editor, dispatch);
      setHasLoaded(true);
    }
  }, [serverData, hasLoaded, editor, dispatch, id]);

  useEffect(() => {
    if (hasLoaded && !documentVersionData.isVersionChange) {
      const removeListener = editor.registerUpdateListener(({ editorState }) => {
        showPagebreak();
        updateHtmlContent(editorState);
      });

      return () => removeListener();
    }
  }, [hasLoaded, documentVersionData, editor, updateHtmlContent]);

  return null;
}

function ReadOnlyPlugin() {
  const [editor] = useLexicalComposerContext();
  const isReadOnly = useSelector((state) => state.editor.isReadOnly);
  const isUiLocked = useSelector((state) => state.editor.isUiLocked);

  useEffect(() => {
    // Read-only when the doc is read-only OR while a cached preview is locked.
    editor.setEditable(!isReadOnly && !isUiLocked);
  }, [editor, isReadOnly, isUiLocked]);

  return null;
}

export default function Editor({ isLinkEditMode, setIsLinkEditMode }) {
  const { historyState } = useSharedHistoryContext();
  const [editor] = useLexicalComposerContext();
  const isReadOnly = useSelector((state) => state.editor.isReadOnly);
  const conflictPreviewVerion = useSelector((state) => state.editor.conflictPreviewVerion);

  const {
    settings: {
      isCollab,
      isAutocomplete,
      isMaxLength,
      isCharLimit,
      isCharLimitUtf8,
      isRichText,
      showTreeView,
      showTableOfContents,
      shouldUseLexicalContextMenu,
      shouldPreserveNewLinesInMarkdown,
      tableCellMerge,
      tableCellBackgroundColor,
    },
  } = useSettings();
  const isEditable = useLexicalEditable();
  const dispatch = useDispatch();
  const placeholder = isCollab
    ? 'Enter some collaborative rich text...'
    : isRichText
      ? 'Enter some rich text...'
      : 'Enter some plain text...';
  const [floatingAnchorElem, setFloatingAnchorElem] =
    useState(null);
  const [isSmallWidthViewport, setIsSmallWidthViewport] =
    useState(false);

  const onRef = (_floatingAnchorElem) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  useEffect(() => {
    const updateViewPortWidth = () => {
      const isNextSmallWidthViewport =
        CAN_USE_DOM && window.matchMedia('(max-width: 1025px)').matches;

      if (isNextSmallWidthViewport !== isSmallWidthViewport) {
        setIsSmallWidthViewport(isNextSmallWidthViewport);
      }
    };
    updateViewPortWidth();
    window.addEventListener('resize', updateViewPortWidth);

    return () => {
      window.removeEventListener('resize', updateViewPortWidth);
    };
  }, [isSmallWidthViewport]);

  const [editorState, setEditorState] = useState();
  function onChange(editorState) {
    setEditorState(editorState);
  }

  useEffect(() => {
    if (editor) {
      dispatch(setLexicalEditor(editor));
    }
  }, [editor, dispatch]);

  return (
    <>
      <div
        id='lexical-editor'
        className={`editor-container ${showTreeView ? 'tree-view' : ''} ${!isRichText ? 'plain-text' : ''
          }`}>
        {isMaxLength && <MaxLengthPlugin maxLength={30} />}
        <DragDropPaste />
        <AutoFocusPlugin />
        <ClearEditorPlugin />
        {/* <ComponentPickerPlugin /> */}
        <EmojiPickerPlugin />
        <AutoEmbedPlugin />

        {/* <MentionsPlugin /> */}
        <EmojisPlugin />
        <HashtagPlugin />
        <KeywordsPlugin />
        <SpeechToTextPlugin />
        <AutoLinkPlugin />
        {/* <CommentPlugin
          providerFactory={isCollab ? createWebsocketProvider : undefined}
        /> */}
        {isRichText ? (
          <>
            {isCollab ? (
              <CollaborationPlugin
                id="main"
                providerFactory={createWebsocketProvider}
                shouldBootstrap={!skipCollaborationInit}
              />
            ) : (
              <HistoryPlugin externalHistoryState={historyState} />
            )}
            <RichTextPlugin
              contentEditable={
                <div className="editor-scroller">
                  {/* Move watermark outside the editable area so it doesn't create an inner scrollbar */}
                  {isReadOnly && !conflictPreviewVerion && <WaterMark />}
                  <div className="editor" ref={onRef} id='lexical-editor-container'>
                    <ContentEditable placeholder={placeholder} />
                  </div>
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <MarkdownShortcutPlugin />
            <CodeHighlightPlugin />
            <ListPlugin />
            <CheckListPlugin />
            <ListMaxIndentLevelPlugin maxDepth={7} />
            <ListRemovePlugin />
            <TablePlugin
              hasCellMerge={tableCellMerge}
              hasCellBackgroundColor={tableCellBackgroundColor}
            />
            <TableCellResizer />
            <TableHoverActionsPlugin />
            <ImagesPlugin />
            <InlineImagePlugin />
            <LinkPlugin />
            <PollPlugin />
            <ChartPlugin />
            <TwitterPlugin />
            <YouTubePlugin />
            <FigmaPlugin />
            <ClickableLinkPlugin disabled={isEditable} />
            <HorizontalRulePlugin />
            <EquationsPlugin />
            <ExcalidrawPlugin />
            <TabFocusPlugin />
            <TabIndentationPlugin />
            <CollapsiblePlugin />
            <PageBreakPlugin />
            <EnsurePagedRootPlugin />
            <PaginationPlugin />
            <HeaderFooterSyncPlugin />
            <LayoutPlugin />
            {floatingAnchorElem && !isSmallWidthViewport && (
              <>
                {/* <DraggableBlockPlugin anchorElem={floatingAnchorElem} /> */}
                {/* <CodeActionMenuPlugin anchorElem={floatingAnchorElem} /> */}
                {/* <FloatingLinkEditorPlugin
                  anchorElem={floatingAnchorElem}
                  isLinkEditMode={isLinkEditMode}
                  setIsLinkEditMode={setIsLinkEditMode}
                /> */}
                <TableCellActionMenuPlugin
                  anchorElem={floatingAnchorElem}
                  cellMerge={true}
                />
                {/* <FloatingTextFormatToolbarPlugin
                  anchorElem={floatingAnchorElem}
                  setIsLinkEditMode={setIsLinkEditMode}
                /> */}
              </>
            )}
          </>
        ) : (
          <>
            <PlainTextPlugin
              contentEditable={<ContentEditable placeholder={placeholder} />}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <HistoryPlugin externalHistoryState={historyState} />
          </>
        )}
        {(isCharLimit || isCharLimitUtf8) && (
          <CharacterLimitPlugin
            charset={isCharLimit ? 'UTF-16' : 'UTF-8'}
            maxLength={5}
          />
        )}
        {isAutocomplete && <AutocompletePlugin />}
        <div>{showTableOfContents && <TableOfContentsPlugin />}</div>
        {!isReadOnly &&
          <ContextMenuPlugin />
        }
        {/* <ActionsPlugin      // editor options
          isRichText={isRichText}               
          shouldPreserveNewLinesInMarkdown={shouldPreserveNewLinesInMarkdown}
        /> */}
        <MyOnChangePlugin onChange={onChange} />
        <MyHtmlPlugin />
        <ReadOnlyPlugin />
        <LexicalSignatures />
      </div >
      {/* <div className="generated-html-container" style={{border:'2px solid black'}}>
        <h2>Generated HTML:</h2>
        <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
        <div>{htmlContent}</div>
      </div> */}
      {/* {showTreeView && <TreeViewPlugin />} */}
    </>
  );
}
